import { Box, Typography } from "@mui/material";
import { getVisualAssetUrl } from "../../../api";
import { wordWrapPassage } from "../utils/passageLineExtract";

// Quill stores passage text as HTML. Convert to plain text with markdown
// bold/italic so passageLineToHtml can re-render it correctly.
const stripHtmlToPassageText = (html) => {
  if (!html || !/<[a-z]/i.test(html)) return html || "";
  return html
    .replace(/<\/p>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, "**$1**")
    .replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, "**$1**")
    .replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, "*$1*")
    .replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, "*$1*")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// Minimal markdown → HTML for passage lines (bold, italic, images)
const passageLineToHtml = (line) => {
  // Escape HTML entities first (before injecting tags)
  let result = line
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Convert markdown images ![alt](url) → <img> (must run before inline formatting)
  result = result.replace(
    /!\[([^\]]*)\]\(([^)\s]+)\)/g,
    (_, alt, src) => {
      let resolvedSrc = src;
      const r2Match = src.match(/https?:\/\/[^"'\s>]*\.r2\.[^"'\s>/]+\/(.+)/);
      const proxyMatch = src.match(/https?:\/\/[^/"'\s>]+\/api\/proxy\/visual\/(.+)/);
      if (r2Match) resolvedSrc = getVisualAssetUrl(r2Match[1]);
      else if (proxyMatch) resolvedSrc = getVisualAssetUrl(proxyMatch[1]);
      return `<img src="${resolvedSrc}" alt="${alt}" style="display:block;max-width:100%;max-height:320px;width:auto;height:auto;border-radius:6px;border:1px solid #e0e0e0;margin:8px 0" onerror="this.style.display='none'">`;
    }
  );

  // Inline formatting (bold / italic)
  result = result
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");

  return result;
};

// Parse line number references from a question text, e.g. "line 5", "lines 3–7", "lines 3 to 7"
export const parseLineRefs = (text) => {
  if (!text) return [];
  const nums = new Set();
  const rangeRe = /lines?\s+(\d+)\s*(?:[-–]|to)\s*(\d+)/gi;
  const singleRe = /lines?\s+(\d+)/gi;
  let m;
  while ((m = rangeRe.exec(text)) !== null) {
    for (let n = +m[1]; n <= +m[2]; n++) nums.add(n);
  }
  singleRe.lastIndex = 0;
  while ((m = singleRe.exec(text)) !== null) nums.add(+m[1]);
  return [...nums];
};

/**
 * PassageWithLineNumbers
 *
 * Renders a reading passage with LEFT-margin line numbers (GL Assessment style).
 *
 * Features:
 * - Detects and renders heading section from "### Title **by Author**" markdown lines
 * - Renders full-line italic blocks "*...*" as a bordered context/summary box
 * - Supports {Ln} markers (e.g. {L5}, {L10}) for explicit line numbers
 * - Supports "1. text" paragraph prefix as an explicit line 1 anchor
 * - Sequential fallback: shows number every 5th non-empty line when no markers present
 *   (set showAllFallbackLineNumbers for admin review so every row has a number)
 *
 * Props:
 *   text          – passage text (may contain ### headings, *italic blocks*, {Ln} markers)
 *   highlightLines – array of line numbers to accent (matched from sub-question refs)
 *   showAllFallbackLineNumbers – when true and no {L…} markers, show a number on every content row
 */
const PassageWithLineNumbers = ({ text, highlightLines = [], showAllFallbackLineNumbers = false, wrapWidth, fontSize = 1.0 }) => {
  const highlightSet = new Set(highlightLines.map(Number));

  // Convert non-breaking spaces (U+00A0 / &nbsp;) to regular spaces. Imported passages
  // store every space as &nbsp;, which blocks word-wrapping (forcing mid-word breaks)
  // and defeats wordWrapPassage's space-based splitting.
  const normalizedText = stripHtmlToPassageText(text || "")
    .replace(/ /g, " ")
    .replace(/&nbsp;/gi, " ");

  const rawLines = normalizedText.split("\n");

  // ── Separate the header block (title, author, context box) from the passage body ──
  const headerItems = []; // { type: 'heading' | 'context', raw: string }
  let bodyStartIdx = 0;
  let i = 0;

  while (i < rawLines.length) {
    const trimmed = rawLines[i].trim();

    if (trimmed === "") {
      // Skip blank lines in the header area
      i++;
      bodyStartIdx = i;
      continue;
    }

    if (/^#{1,6}\s/.test(trimmed)) {
      // Markdown heading line
      headerItems.push({ type: "heading", raw: trimmed });
      i++;
      bodyStartIdx = i;
      continue;
    }

    // Full-line italic block: starts with * (not **) and ends with * (not **), ≥30 chars
    if (
      /^\*[^*]/.test(trimmed) &&
      trimmed.endsWith("*") &&
      !trimmed.endsWith("**") &&
      trimmed.length > 30
    ) {
      headerItems.push({ type: "context", raw: trimmed });
      i++;
      bodyStartIdx = i;
      continue;
    }

    // First non-header content line — passage body starts here
    break;
  }

  // Word-wrap flat passages (no newlines) so line numbers correspond to exam paper layout.
  const bodyText = wordWrapPassage(rawLines.slice(bodyStartIdx).join("\n"), wrapWidth);

  // Pre-process: convert "1. text" paragraph-start prefix → "{L1}text"
  // so the first line's anchor is shown in the left margin.
  const bodyProcessed = bodyText.replace(/^(\d+)\.\s/gm, (_, n) => `{L${n}}`);

  const hasMarkers = /\{L\d+\}/.test(bodyProcessed);

  const processed = (() => {
    if (!hasMarkers) {
      let count = 0;
      return bodyProcessed.split("\n").map((line) => {
        const hasContent = line.trim().length > 0;
        const isImageLine = /^!\[[^\]]*\]\([^)]+\)\s*$/.test(line.trim());
        if (hasContent && !isImageLine) count++;
        return {
          html: hasContent ? passageLineToHtml(line) : null,
          num: hasContent && !isImageLine ? count : null,
          explicitNum: null,
        };
      });
    }

    const result = [];
    bodyProcessed.split("\n").forEach((rawLine) => {
      // Split at each {L#} marker so mid-line markers (caused by Quill collapsing \n
      // into one paragraph) each create their own numbered row instead of being lost.
      const segs = rawLine.split(/(?=\{L\d+\})/);
      segs.forEach((seg) => {
        if (!seg) return;
        const mMatch = seg.match(/^\{L(\d+)\}/);
        const lineNum = mMatch ? parseInt(mMatch[1], 10) : null;
        const cleanSeg = seg.replace(/\{L\d+\}/g, "").trimEnd();
        const hasContent = cleanSeg.trim().length > 0;
        if (hasContent || lineNum !== null) {
          result.push({
            html: hasContent ? passageLineToHtml(cleanSeg) : null,
            num: null,
            explicitNum: lineNum,
          });
        }
      });
    });
    return result;
  })();

  // ── Render the header section (book title, author line, context box) ──
  const renderHeader = () => {
    if (headerItems.length === 0) return null;
    return (
      <Box sx={{ mb: 1.5 }}>
        {headerItems.map((item, idx) => {
          if (item.type === "heading") {
            // Strip # prefix: "### The Swiss Family Robinson **by Johann David Wyss**"
            const headingContent = item.raw.replace(/^#{1,6}\s+/, "");
            // Split on **bold** segments to separate title from author attribution
            const segments = headingContent
              .split(/(\*\*[^*]+\*\*)/g)
              .filter(Boolean);
            return (
              <Box key={idx} sx={{ textAlign: "center", mb: 0.25 }}>
                {segments.map((seg, si) => {
                  const isBold = seg.startsWith("**") && seg.endsWith("**");
                  const segText = isBold ? seg.slice(2, -2) : seg.trim();
                  if (!segText) return null;
                  return (
                    <Typography
                      key={si}
                      sx={{
                        display: "block",
                        fontFamily: "Georgia, serif",
                        // Non-bold = book title (large, bold)
                        // Bold = author attribution (smaller, italic, lighter)
                        fontWeight: isBold ? 400 : 700,
                        fontStyle: isBold ? "italic" : "normal",
                        fontSize: isBold ? "1.0rem" : "1.25rem",
                        color: "#1a1a1a",
                        lineHeight: 1.4,
                      }}
                    >
                      {segText}
                    </Typography>
                  );
                })}
              </Box>
            );
          }

          if (item.type === "context") {
            // Strip surrounding * and render as a bordered context/summary box
            const contextText = item.raw.slice(1, -1);
            return (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #bbb",
                  borderRadius: 0.5,
                  p: 1.5,
                  my: 1.5,
                  bgcolor: "#fefefe",
                }}
              >
                <Box
                  sx={{
                    fontStyle: "italic",
                    fontSize: `${(fontSize * 0.88).toFixed(2)}rem`,
                    color: "#333",
                    lineHeight: 1.75,
                    fontFamily: "Georgia, serif",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: passageLineToHtml(contextText),
                  }}
                />
              </Box>
            );
          }

          return null;
        })}
      </Box>
    );
  };

  return (
    <Box>
      {renderHeader()}
      {processed.map((item, idx) => {
        const lineNumForHighlight = hasMarkers ? item.explicitNum : item.num;
        const isHighlighted =
          lineNumForHighlight != null && highlightSet.has(lineNumForHighlight);
        const showNum = hasMarkers
          ? item.explicitNum != null
          : item.num != null &&
            (showAllFallbackLineNumbers || item.num % 5 === 0 || isHighlighted);
        const displayNum = hasMarkers ? item.explicitNum : item.num;

        return (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              minHeight: item.html ? undefined : "0.6em",
              ...(isHighlighted ? { bgcolor: "#fff3e0" } : {}),
            }}
          >
            {/* Line number — LEFT margin (GL Assessment style) */}
            <Box
              sx={{
                minWidth: 28,
                textAlign: "right",
                pr: 1.5,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                lineHeight: 1.9,
                flexShrink: 0,
                userSelect: "none",
                pt: "3px",
                color: isHighlighted ? "#e65100" : "#666",
                fontWeight: isHighlighted ? 700 : 400,
              }}
            >
              {showNum ? displayNum : ""}
            </Box>
            {/* Passage text */}
            <Box
              sx={{
                flex: 1,
                fontFamily: "Georgia, serif",
                fontSize: `${fontSize}rem`,
                lineHeight: 1.9,
                color: "#2a2a2a",
                px: isHighlighted ? 0.5 : 0,
              }}
              dangerouslySetInnerHTML={{ __html: item.html || "" }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default PassageWithLineNumbers;
